import { FlowElement, FlowElementWithProps, FlowQuestion } from 'src/lib/flow/FlowQuestion';
import { SavedAnswer } from 'src/store/sellerInput';

import type { FlowContext } from './index';

type PageFields = {
  slug: () => string;
  isEnabled: (props: FlowContext) => boolean;
  questions: (props: FlowContext) => Array<FlowQuestion>;
  autoAdvance: (props: FlowContext) => boolean;
  title?: (props: FlowContext) => string | undefined;
  subtitle?: (props: FlowContext) => string | undefined;
  subtitleElement?: (props: FlowContext) => FlowElement | FlowElementWithProps | undefined;
  image?: (props: FlowContext) => string | undefined;
  nextText?: (props: FlowContext) => string | undefined;
  tooltip?: (props: FlowContext) => string | undefined;
  carrot?: (props: FlowContext) => string | undefined;
  note?: (props: FlowContext) => string | undefined;
  showPrivacyNotice?: (props: FlowContext) => boolean;
  lastCarrot?: (props: FlowContext) => boolean;
  hideProgress?: (props: FlowContext) => boolean;

  // True if in addition to the default next button we should display a button that
  // says "Skip" and allows the user to move to the next page
  showSkipButton?: (props: FlowContext) => boolean;

  // This is used to exit the flow, if it returns a string we will send the user to that path
  // if it is null the user will get the next enabled page in the page set
  exitPath?: (ctx: FlowContext) => string | null;

  // This works in the two column layout to show a second action that when click will add
  // the provided answer set to the user's saved answers and then move to the next page
  secondaryAction?: (props: FlowContext) =>
    | {
        label: string;
        answerSet: Array<{ key: string; answer: SavedAnswer | null }>;
      }
    | undefined;
};

export type FlowPage = Readonly<PageFields>;

export type FlowPageSet = Array<FlowPage>;

export interface EvaluatedPageFields {
  slug: string;
  title?: string;
  subtitle?: string;
  subtitleElement?: FlowElement | FlowElementWithProps;
  image?: string;
  isEnabled: boolean;
  questions: Array<FlowQuestion>;
  nextText?: string;
  tooltip?: string;
  carrot?: string;
  note?: string;
  showPrivacyNotice?: boolean;
  showSkipButton?: boolean;
  lastCarrot?: boolean;
  autoAdvance: boolean;
  hideProgress: boolean;
  exitPath?: string;
  secondaryAction?: {
    label: string;
    answerSet: Array<{ key: string; answer: SavedAnswer | null }>;
  };
}

export type EvaluatedFlowPage = Readonly<EvaluatedPageFields>;

/* Evaluates a page using context data to get the final version of the page */
export function evaluatePage(page: FlowPage, context: FlowContext) {
  const evaluatedPage = {};
  for (const [key, value] of Object.entries(page)) {
    evaluatedPage[key] = value ? value(context) : undefined;
  }
  return evaluatedPage as EvaluatedFlowPage;
}

/**
 * This allows us to compose a new page based off an existing page
 *
 * @param overrides - Accepts either a partial FlowPage or a function that gets
 * the original base page as a parameter and returns a partial FlowPage. This
 * second way lets us call the original implementation instead of completely
 * replacing when needed.
 *
 * Examples:
 * ```
 * const newPage = composePage(relationshipPage, {
 *   title: () => "My new title!"
 * })
 *
 * // or if you want to fallback to existing page functionality in an override:
 * const newPageExtended = composePage(relationshipPage, (basePage) => {
 *   title: (ctx) => {
 *     if (ctx.experimentFlag.myNewTest == "treatment") {
 *       return "My New title!"
 *     }
 *     return basePage.title(ctx)
 *   }
 * })
 *
 *
 * ```
 *
 *
 * @returns the new flow page
 */
export const composePage = (
  base: FlowPage,
  overrides: Partial<FlowPage> | ((b: FlowPage) => Partial<FlowPage>),
): FlowPage => {
  let newVals = overrides;
  if (typeof overrides == 'function') {
    newVals = overrides(base);
  }
  return {
    ...base,
    ...newVals,
  };
};

type NewFlowPage = {
  [P in keyof FlowPage]: FlowPage[P] | ReturnType<Required<FlowPage>[P]>;
};

/**
 * This is a helper function that allows for an easy way to create new pages,
 * instead of creating a FlowPage directly, which requires every page attribute
 * be wrapped in a function:
 *
 * ```
 * const newPage: FlowPage = {
 *   slug: () => "my-slug"
 *   title: (ctx: FlowContext) => {
 *     if (ctx.answers["seller.say_hi"]) {
 *       return "Hello"
 *     }
 *     return "Good evening"
 *   }
 *   ...other keys...
 * }
 * ```
 *
 * You can do this:
 *
 * ```
 * const newPage = newFlowPage({
 *   slug: "my-slug"
 *   title: (ctx: FlowContext) => {
 *     if (ctx.answers["seller.say_hi"]) {
 *       return "Hello"
 *     }
 *     return "Good evening"
 *   }
 *   ...other keys...
 * })
 * ```
 *
 * @params The input is an object that has all the required and possibly
 * optional keys of FlowPage, each key can either be the raw value needed or
 * a function that accepts FlowContext and returns the desired value.
 */
export const newFlowPage = (p: NewFlowPage): FlowPage => {
  const entries = Object.entries(p);
  // loop over our object and when needed wrap with a
  // function to conform to FlowPage
  return entries.reduce((newPage, [key, val]) => {
    if (typeof val == 'function') {
      newPage[key] = val;
    } else {
      newPage[key] = () => val;
    }
    return newPage;
  }, {} as FlowPage);
};
